import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { articlesAPI } from '@/lib/api';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft,
  Calendar,
  Eye,
  User,
  Share2,
  Facebook,
  Loader2,
  BookOpen,
  ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

const ArticleDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArticle();
  }, [slug]);

  const fetchArticle = async () => {
    try {
      const res = await articlesAPI.getBySlug(slug);
      setArticle(res.data);
      
      // Fetch related articles from same category
      const allRes = await articlesAPI.getAll({ category: res.data.category, limit: 4 });
      const related = (allRes.data || []).filter(a => a.slug !== slug).slice(0, 3);
      setRelatedArticles(related);
    } catch (error) {
      console.error('Error fetching article:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (categoryId) => {
    const colors = {
      study_tips: 'bg-blue-100 text-blue-600',
      visa_guides: 'bg-green-100 text-green-600',
      country_guides: 'bg-purple-100 text-purple-600',
      success_stories: 'bg-teal-100 text-teal-600'
    };
    return colors[categoryId] || 'bg-gray-200 text-gray-500';
  };

  const getCategoryName = (categoryId) => {
    const names = {
      study_tips: 'Study Tips',
      visa_guides: 'Visa Guides',
      country_guides: 'Country Guides',
      success_stories: 'Success Stories'
    };
    return names[categoryId] || categoryId;
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: article.title,
          text: article.excerpt,
          url: url
        });
      } catch (error) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-[#f8f9fa] dark:bg-slate-900 transition-colors">
        <Navbar />
        <main className="pt-20 pb-12">
          <div className="max-w-4xl mx-auto px-4 py-20 text-center">
            <BookOpen className="w-16 h-16 text-gray-500 dark:text-gray-400 mx-auto mb-4" />
            <h1 className="text-2xl font-semibold text-gray-800 dark:text-white mb-2">Article Not Found</h1>
            <p className="text-gray-500 dark:text-gray-400 mb-6">The article you&apos;re looking for doesn&apos;t exist.</p>
            <Button onClick={() => navigate('/blog')} className="bg-teal-600 text-white hover:bg-teal-700">
              Browse All Articles
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fa] dark:bg-slate-900 transition-colors">
      <Navbar />
      
      <main className="pt-20 pb-12">
        {/* Article Header */}
        <div className="bg-gradient-to-r from-teal-100/80 dark:from-slate-800 via-[#e9ecef] dark:via-slate-800 to-[#f1f3f5] dark:to-slate-900 py-12 transition-colors">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <button 
              onClick={() => navigate('/blog')}
              className="flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </button>
            
            <Badge className={`mb-4 ${getCategoryColor(article.category)}`}>
              {getCategoryName(article.category)}
            </Badge>
            
            <h1 className="text-3xl md:text-4xl font-semibold text-gray-800 dark:text-white mb-4">{article.title}</h1>
            
            <div className="flex flex-wrap items-center gap-4 text-gray-500 dark:text-gray-400">
              <span className="flex items-center gap-2">
                <User className="w-4 h-4" />
                {article.author_name}
              </span>
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {new Date(article.created_at).toLocaleDateString('en-GB', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </span>
              <span className="flex items-center gap-2">
                <Eye className="w-4 h-4" />
                {article.views} views
              </span>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid lg:grid-cols-4 gap-8">
            {/* Article Content */}
            <div className="lg:col-span-3">
              {/* Featured Image */}
              {article.featured_image && (
                <div className="rounded-xl overflow-hidden mb-8">
                  <img 
                    src={article.featured_image} 
                    alt={article.title}
                    className="w-full h-auto"
                  />
                </div>
              )}

              {/* Article Body */}
              <article className="prose prose-lg prose-invert max-w-none">
                <div 
                  dangerouslySetInnerHTML={{ __html: article.content }}
                  className="text-gray-600 leading-relaxed
                    [&>h2]:text-2xl [&>h2]:font-semibold [&>h2]:text-gray-800 [&>h2]:mt-8 [&>h2]:mb-4
                    [&>h3]:text-xl [&>h3]:font-semibold [&>h3]:text-gray-800 [&>h3]:mt-6 [&>h3]:mb-3
                    [&>p]:mb-4
                    [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-4 [&>ul>li]:mb-2
                    [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-4 [&>ol>li]:mb-2
                    [&>blockquote]:border-l-4 [&>blockquote]:border-teal-500 [&>blockquote]:pl-4 [&>blockquote]:italic [&>blockquote]:text-gray-500
                  "
                />
              </article>

              {/* Tags */}
              {article.tags?.length > 0 && (
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-500 mb-3">Tags</h4>
                  <div className="flex flex-wrap gap-2">
                    {article.tags.map((tag, idx) => (
                      <Badge key={idx} variant="outline" className="border-gray-300 text-gray-600 dark:text-gray-300">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Share */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-500 mb-3">Share this article</h4>
                <div className="flex gap-3">
                  <Button 
                    onClick={handleShare}
                    variant="outline" 
                    size="sm"
                    className="border-gray-300 text-gray-600 hover:bg-gray-100"
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Share
                  </Button>
                  <a 
                    href={`https://wa.me/?text=${encodeURIComponent(article.title + ' - ' + window.location.href)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="border-green-500/30 text-green-600 hover:bg-green-500/10"
                    >
                      WhatsApp
                    </Button>
                  </a>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1 space-y-6">
              {/* CTA Card */}
              <div className="bg-white border border-gray-200 rounded-xl rounded-xl p-5">
                <h4 className="font-semibold text-gray-800 mb-3">Need Personalized Advice?</h4>
                <p className="text-gray-500 text-sm mb-4">
                  Our expert counselors can help you with your specific situation.
                </p>
                <Link to="/help">
                  <Button className="w-full bg-teal-600 text-white hover:bg-teal-700 text-sm">
                    Contact Us
                  </Button>
                </Link>
              </div>

              {/* Related Articles */}
              {relatedArticles.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl rounded-xl p-5">
                  <h4 className="font-semibold text-gray-800 mb-4">Related Articles</h4>
                  <div className="space-y-4">
                    {relatedArticles.map((related) => (
                      <Link key={related.id} to={`/blog/${related.slug}`}>
                        <div className="group">
                          <h5 className="text-sm text-gray-600 group-hover:text-teal-600 transition-colors line-clamp-2">
                            {related.title}
                          </h5>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(related.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* More Articles */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-800 dark:text-white">More from the Blog</h3>
              <Link to="/blog">
                <Button variant="ghost" className="text-teal-600 hover:text-teal-700">
                  View All
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            
            {relatedArticles.length > 0 && (
              <div className="grid md:grid-cols-3 gap-6">
                {relatedArticles.map((related) => (
                  <Link key={related.id} to={`/blog/${related.slug}`}>
                    <div className="bg-white border border-gray-200 rounded-xl rounded-xl p-4 hover:border-teal-300 transition-colors">
                      <Badge className={`mb-2 text-xs ${getCategoryColor(related.category)}`}>
                        {getCategoryName(related.category)}
                      </Badge>
                      <h4 className="text-gray-800 font-medium line-clamp-2">{related.title}</h4>
                      <p className="text-xs text-gray-500 mt-2">
                        {new Date(related.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ArticleDetailPage;
